import { ComputedRef } from 'vue'

import { WeeklyRating } from '@/domain/entities/dashboard/weeklyRating'
import { User } from '@/domain/entities/user/user'
import { DisplayLanguageType } from '@/domain/value_objects/language/displayLanguageType'
import { WeeklyRatingViewModel } from '@/presentation/viewmodels/dashboard/weeklyRatingViewModel'
import { getLineChartOptions } from '@/util/dashboard/lineChartOptions'

export class WeeklyRatingPresenter {
  private viewModel: WeeklyRatingViewModel

  constructor(viewModel: WeeklyRatingViewModel) {
    this.viewModel = viewModel
  }

  get userWeeklyRatingList(): ComputedRef<WeeklyRating[][]> {
    return this.viewModel.userWeeklyRatingList
  }

  get chartData(): ComputedRef<{ labelChartData: number[]; weeklyRatingChartData: number[][]; maxRating: number }> {
    return this.viewModel.chartData
  }

  get chartSeries(): ComputedRef<any[]> {
    return this.viewModel.chartSeries
  }

  get chartOptions(): ComputedRef<any> {
    return this.viewModel.chartOptions
  }

  get hasData(): ComputedRef<boolean> {
    return this.viewModel.hasData
  }

  get isLoading(): ComputedRef<boolean> {
    return this.viewModel.isLoading
  }

  get error(): ComputedRef<string | null> {
    return this.viewModel.error
  }

  addUserWeeklyRating = async (userId: string) => {
    await this.viewModel.addUserWeeklyRating(userId)
  }

  removeUserWeeklyRating = () => {
    this.viewModel.removeUserWeeklyRating()
  }

  removeTargetUsers = () => {
    this.viewModel.removeTargetUsers()
  }

  initChart = (
    userList: User[],
    color: string,
    otherColor: string,
    displayLanguage: DisplayLanguageType,
    isLightMode: boolean
  ) => {
    const chartData = this.getWeeklyDataOfChart()

    if (chartData.labelChartData.length == 0) {
      this.viewModel.setHasData(false)
      return
    }

    try {
      const chartSeries = []
      for (let i = 0; i < chartData.weeklyRatingChartData.length; i++) {
        const roundedArray = chartData.weeklyRatingChartData[i].map(num => num.toFixed(2))
        chartSeries.push(this.getChartData(userList[i].id, roundedArray, i == 0 ? color : otherColor))
      }
      const chartOptions = getLineChartOptions(
        displayLanguage == 'Japanese' ? '毎週のレーティング' : 'Weekly Rating',
        color,
        chartData.labelChartData,
        isLightMode,
        false
      )
      this.viewModel.setChartData(chartData)
      this.viewModel.setChartSeries(chartSeries)
      this.viewModel.setChartOptions(chartOptions)
      this.viewModel.setHasData(true)
    } catch (e) {
      this.viewModel.setHasData(false)
    }
  }

  private getChartData = (name: string, values: any, color: string) => {
    return {
      name: name,
      data: values,
      color: color
    }
  }

  // チャート用のデータを取得
  getWeeklyDataOfChart = () => {
    const userWeekly = this.viewModel.userWeeklyRatingList.value

    const labelChartData = this.getDateList(userWeekly)
    const weeklyRatingChartData: number[][] = []
    let maxRating = 0

    for (let i = 0; i < userWeekly.length; i++) {
      const weekly = this.getUserWeeklyData(userWeekly[i], labelChartData)
      weeklyRatingChartData.push(weekly)

      if (weekly.length == 0) weeklyRatingChartData[i].push(0)

      maxRating = Math.max(
        maxRating,
        weekly.map(v => v).reduce((a, b) => Math.max(a, b))
      )
    }

    return { labelChartData, weeklyRatingChartData, maxRating }
  }

  // 特定のユーザーのデータを取得
  getUserWeeklyData = (weekly: WeeklyRating[], labelChartData: number[]) => {
    const weeklyRatingChartData: number[] = []

    let weeklyIndex = 0
    for (let i = 0; i < labelChartData.length; i++) {
      if (weeklyIndex >= weekly.length) {
        weeklyRatingChartData.push(0)
      } else if (weekly[weeklyIndex].date == labelChartData[i]) {
        weeklyRatingChartData.push(weekly[weeklyIndex].rating)
        weeklyIndex++
      } else {
        weeklyRatingChartData.push(0)
      }
    }

    return weeklyRatingChartData
  }

  // 不足した日付を埋める
  getDateList = (weekly: WeeklyRating[][]) => {
    const set = new Set<number>([])

    for (let i = 0; i < weekly.length; i++) {
      for (let j = 0; j < weekly[i].length; j++) {
        set.add(weekly[i][j].date)
      }
    }

    const list = Array.from(set)
    list.sort()

    return list
  }
}
